/* eslint-disable react-hooks/exhaustive-deps */
import { InfoCircleFilled } from "@ant-design/icons";
import { Pie } from "@ant-design/plots";
import { Select, DatePicker, Descriptions, Input } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { useGraphTheme } from "../../../../hooks/useGraphTheme";
import { showErrorNotification } from "../../../../utils/notification";
import { clamp } from "../../../../utils/responsiveHelpers";
import TooltipWrapper from "../../../common-utils/TooltipWrapper";
import ShowDuplicateQueryDetailsModal from "../../helper/ShowDuplicateQueryDetailsModal";
import ShowTransferredQueryDetailsModal from "../../helper/ShowTransferredQueryDetailsModal";
import ShowUnsatisfiedQueryDetailsModal from "../../helper/ShowUnsatisfiedQueryDetailsModal";
import { unsatisfiedReasonsPieConfig } from "../../helper/ShowUnsatisfiedQueryDetailsModal/graphConfigs";
import QueryAnalyticsGenericBarGraph from "../QueryAnalyticsGenericBarGraph";

const DiscordQueryAnalyticsDash = ({ ROOT_URL }) => {
  const graphTheme = useGraphTheme();
  const BASE_BACKEND_URL = "admin/discord-analytics/query-analytics";
  const currentDate = new Date();
  const oneMonthAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );

  // Filter configs
  const [courseNamesList, setCourseNameList] = useState([]);
  const [filter, setFilter] = useState({
    course: "All",
    startDate: oneMonthAgo,
    endDate: currentDate
  });

  // Graphs Data
  const [dailyQueryStatsPoints, setDailyQueryStatsPoints] = useState([]);
  const [_, setDailyUnsatisfiedQueryStatsPoints] = useState([]);
  const [dailyQueryResponseTimePoints, setDailyQueryResponseTimePoints] = useState([]);
  const [dailyQueryResponseTimePercentilePoints, setDailyQueryResponseTimePercentilePoints] =
    useState([]);
  const [
    queryCountGreaterThanInputResponseTimeLimitPoints,
    setQueryCountGreaterThanInputResponseTimeLimitPoints
  ] = useState([]);
  const [dailyResponseTimeBucketedHourly, setDailyResponseTimeBucketedHourly] = useState([]);
  const [dailyQueriesAvgBucketedHourly, setDailyQueriesAvgBucketedHourly] = useState([]);

  // Aggregated Data
  const [dailySatisfiedQueries, setDailySatisfiedQueries] = useState(0);
  const [dailyUnSatisfiedQueries, setDailyUnSatisfiedQueries] = useState(0);
  const [totalOpenQueries, setTotalOpenQueries] = useState(0);
  const [totalInProgressQueries, setTotalInProgressQueries] = useState(0);
  const [totalInReviewQueries, setTotalInReviewQueries] = useState(0);
  const [totalUnratedQueries, setTotalUnratedQueries] = useState(0);
  const [weightedResponseTimeAvg, setWeightedResponseTimeAvg] = useState(0);
  const [dailyQueryCountAverage, setDailyQueryCountAverage] = useState(0);
  const [totalQueries, setTotalQueries] = useState(0);
  const [totalTransfers, setTotalTransfers] = useState(0);
  const [totalAutoThanked, setTotalAutoThanked] = useState(0);
  const [totalManuallyThanked, setTotalManuallyThanked] = useState(0);
  const [totalDuplicates, setTotalDuplicates] = useState(0);
  const [unsatisfiedReasonsData, setUnsatisfiedReasonData] = useState([]);

  const [queryResponseTimePercentile, setQueryResponseTimePercentile] = useState(90);
  const [queryResponseTimeLimitMins, setQueryResponseTimeLimitMins] = useState(15);

  const [unSatisfiedQueriesList, setUnSatisfiedQueriesList] = useState();
  const [isUnsatisfiedQueryListModalOpen, setIsUnsatisfiedQueryListModalOpen] = useState(false);
  const [transferredQueriesList, setTransferredQueriesList] = useState();
  const [isTransferredQueryListModalOpen, setIsTransferredQueryListModalOpen] = useState(false);
  const [duplicateQueriesList, setDuplicateQueriesList] = useState();
  const [isDuplicateQueryListModalOpen, setIsDuplicateQueryListModalOpen] = useState(false);
  // This state is used because `queryResponseTimeLimitMins` is a input field and every
  // input change will trigger the API which is not efficient and race b/w multiple API
  // calls can render stale data. This state will be set on enter in the input box and
  // only then the API will be called.
  const [
    shouldTriggerQueryResponseTimeLimitMinsDataAPI,
    setShouldTriggerQueryResponseTimeLimitMinsDataAPI
  ] = useState(true);

  const infoComponentForExcludedQueries = () => {
    return (
      <TooltipWrapper
        title="Queries from 3AM to 8AM IST are excluded"
        body={<InfoCircleFilled />}
      />
    );
  };

  const fetchCourseNamesList = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/courses-list`, {})
      .then((res) => {
        const { coursesList } = res.data;
        setCourseNameList(coursesList);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailyQueryAnalytics = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/daily-analytics`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const {
          dailyQueryStats: dailyQueryStatsPoints,
          totalSatisfied,
          totalUnSatisfied,
          totalQueries,
          openQueries,
          unratedQueries,
          inProgressQueriesCount,
          inReviewQueriesCount,
          dailyQueryCountAvg,
          totalTransfers,
          totalAutoThanked,
          totalManuallyThanked,
          totalDuplicates
        } = res.data;
        setDailyQueryStatsPoints(dailyQueryStatsPoints);
        setDailySatisfiedQueries(totalSatisfied);
        setDailyUnSatisfiedQueries(totalUnSatisfied);
        setTotalQueries(totalQueries);
        setTotalOpenQueries(openQueries);
        setTotalUnratedQueries(unratedQueries);
        setTotalInProgressQueries(inProgressQueriesCount);
        setTotalInReviewQueries(inReviewQueriesCount);
        setDailyQueryCountAverage(dailyQueryCountAvg);
        setTotalTransfers(totalTransfers);
        setTotalAutoThanked(totalAutoThanked);
        setTotalManuallyThanked(totalManuallyThanked);
        setTotalDuplicates(totalDuplicates);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchUnsatisfiedQueryDetails = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/unsatisfied-queries`, {
        params: { filter }
      })
      .then((res) => {
        setUnSatisfiedQueriesList(res.data);
        setIsUnsatisfiedQueryListModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailyQueryResponseTimeAnalytics = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/daily-pick-time-analytics`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { dailyQueryResolutionTimeStats, weightedResponseTimeAvg } = res.data;
        setDailyQueryResponseTimePoints(dailyQueryResolutionTimeStats);
        setWeightedResponseTimeAvg(weightedResponseTimeAvg);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchQueryResponseTimePercentileData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/response-time-percentile`, {
        params: {
          filter,
          percentile: queryResponseTimePercentile
        }
      })
      .then((res) => {
        const { queryResponseTimePercentilePoints } = res.data;
        setDailyQueryResponseTimePercentilePoints(queryResponseTimePercentilePoints);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailyQueryUnsatisfiedAnalytics = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/daily-unsatisfied-analytics`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { resolvedUnsatisfiedQueriesStats } = res.data;
        setDailyUnsatisfiedQueryStatsPoints(resolvedUnsatisfiedQueriesStats);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailyQueryResponseTimeAnalyticsBucketedHourly = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/response-time-average-hourly`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { queryResponseTimeHourlyPoints } = res.data;
        setDailyResponseTimeBucketedHourly(queryResponseTimeHourlyPoints);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchQueryCountAvgAnalyticsBucketedHourly = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/count-average-hourly`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { avgQueriesHourlyPoints } = res.data;
        setDailyQueriesAvgBucketedHourly(avgQueriesHourlyPoints);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailyQueryCountGreaterThanResponseTimeLimit = () => {
    if (shouldTriggerQueryResponseTimeLimitMinsDataAPI) {
      axios
        .get(`${ROOT_URL}/${BASE_BACKEND_URL}/count-greater-than-response-time-limit`, {
          params: {
            filter,
            queryResponseTimeLimitMins
          }
        })
        .then((res) => {
          const { queryCountGreaterThanInputResponseTimeLimitPoints } = res.data;
          setQueryCountGreaterThanInputResponseTimeLimitPoints(
            queryCountGreaterThanInputResponseTimeLimitPoints
          );
          setShouldTriggerQueryResponseTimeLimitMinsDataAPI(false);
        })
        .catch((err) => {
          showErrorNotification(err);
        });
    }
  };

  const fetchUnsatisfiedReasonStats = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/unsatisfied-queries-reasons-analytics`, {
        params: {
          filter
        }
      })
      .then((res) => {
        setUnsatisfiedReasonData(res.data);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchTransferredQueryDetails = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/transferred-queries`, {
        params: { filter }
      })
      .then((res) => {
        setTransferredQueriesList(res.data);
        setIsTransferredQueryListModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDuplicateQueryDetails = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/duplicate-queries`, {
        params: { filter }
      })
      .then((res) => {
        setDuplicateQueriesList(res.data);
        setIsDuplicateQueryListModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  useEffect(() => {
    fetchCourseNamesList();
  }, []);

  useEffect(() => {
    fetchDailyQueryAnalytics();
    fetchDailyQueryResponseTimeAnalytics();
    fetchDailyQueryUnsatisfiedAnalytics();
    fetchDailyQueryResponseTimeAnalyticsBucketedHourly();
    fetchQueryCountAvgAnalyticsBucketedHourly();
    fetchUnsatisfiedReasonStats();
  }, [filter]);

  useEffect(() => {
    fetchQueryResponseTimePercentileData();
  }, [filter, queryResponseTimePercentile]);

  useEffect(() => {
    fetchDailyQueryCountGreaterThanResponseTimeLimit();
  }, [filter, shouldTriggerQueryResponseTimeLimitMinsDataAPI]);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "5px"
        }}
      >
        <Select
          placeholder="Select Course"
          defaultValue="All"
          style={{ width: "min(200px, 100%)", marginBottom: 10 }}
          onChange={(value) => {
            setFilter((prevFilter) => ({
              ...prevFilter,
              course: value
            }));
          }}
        >
          {(courseNamesList || []).map((item) => (
            <Select.Option value={item.label} key={item.key}>
              {item.label}
            </Select.Option>
          ))}
        </Select>
        <DatePicker.RangePicker
          style={{ width: "50%" }}
          value={
            filter.startDate && filter.endDate
              ? [dayjs(filter.startDate), dayjs(filter.endDate)]
              : []
          }
          onChange={(dates) => {
            if (dates) {
              setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: dates[0].clone().startOf("day").toDate(),
                endDate: dates[1].clone().endOf("day").toDate()
              }));
            } else {
              setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: null,
                endDate: null
              }));
            }
          }}
        />
      </div>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Query Stats in selected date range</h3>
        <Descriptions title="Counts" bordered column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Total Queries">{totalQueries}</Descriptions.Item>
          <Descriptions.Item label="Satisfied">{dailySatisfiedQueries}</Descriptions.Item>
          <Descriptions.Item label="Un-Satisfied">
            {dailyUnSatisfiedQueries}{" "}
            <InfoCircleFilled
              onClick={() => {
                fetchUnsatisfiedQueryDetails();
              }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Unrated">{totalUnratedQueries}</Descriptions.Item>
          <Descriptions.Item label="Open">{totalOpenQueries}</Descriptions.Item>
          <Descriptions.Item label="In Progress">{totalInProgressQueries}</Descriptions.Item>
          <Descriptions.Item label="Under Review">{totalInReviewQueries}</Descriptions.Item>
          <Descriptions.Item label="Response Time Avg (minutes)">
            {weightedResponseTimeAvg} {infoComponentForExcludedQueries()}
          </Descriptions.Item>
          <Descriptions.Item label="Daily Queries Avg">{dailyQueryCountAverage}</Descriptions.Item>
          <Descriptions.Item label="Transferred Query">
            {totalTransfers}{" "}
            <InfoCircleFilled
              onClick={() => {
                fetchTransferredQueryDetails();
              }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Auto Thanked Query">{totalAutoThanked}</Descriptions.Item>
          <Descriptions.Item label="Manual Thanked Query">{totalManuallyThanked}</Descriptions.Item>
          <Descriptions.Item label="Duplicate Query">
            {totalDuplicates}{" "}
            <InfoCircleFilled
              onClick={() => {
                fetchDuplicateQueryDetails();
              }}
            />
          </Descriptions.Item>
        </Descriptions>
        <h3>Daily Query Count</h3>
        <QueryAnalyticsGenericBarGraph dataPoints={dailyQueryStatsPoints} />
      </div>

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Unsatisfied Queries Reasons Stats</h3>
        <Pie
          key={JSON.stringify(unsatisfiedReasonsData)}
          data={unsatisfiedReasonsData || []}
          {...unsatisfiedReasonsPieConfig}
          {...graphTheme}
          legend={{
            ...unsatisfiedReasonsPieConfig.legend,
            ...graphTheme.legend
          }}
        />
      </div>

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Average Query Response Time (Y-minutes) {infoComponentForExcludedQueries()}</h3>
        <QueryAnalyticsGenericBarGraph dataPoints={dailyQueryResponseTimePoints} />
      </div>

      <h3>Query Response Time Percentile Data {infoComponentForExcludedQueries()}</h3>
      <Select
        placeholder="Select Percentile"
        style={{ width: "min(200px, 100%)", marginBottom: 10 }}
        onChange={(value) => {
          setQueryResponseTimePercentile(value);
        }}
        value={queryResponseTimePercentile}
      >
        {[50, 60, 75, 80, 90, 95, 99].map((item) => (
          <Select.Option value={item} key={item}>
            {item}
          </Select.Option>
        ))}
      </Select>
      <QueryAnalyticsGenericBarGraph dataPoints={dailyQueryResponseTimePercentilePoints} />

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Avg Queries % (IST HOURS)</h3>
        <QueryAnalyticsGenericBarGraph
          dataPoints={dailyQueriesAvgBucketedHourly}
          xAxisFieldName={"hourSlab"}
        />
      </div>

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Response Time Hourly (Y-minutes) (IST HOURS)</h3>
        <QueryAnalyticsGenericBarGraph
          dataPoints={dailyResponseTimeBucketedHourly}
          xAxisFieldName={"hourSlab"}
        />
      </div>

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Query Count Greater Than Input Response Time Limit</h3>
        Input response time limit, press enter post input to view the results.
        <Input
          style={{ width: "min(200px, 100%)", marginBottom: 10 }}
          placeholder="Any number"
          onPressEnter={() => {
            setShouldTriggerQueryResponseTimeLimitMinsDataAPI(true);
          }}
          onChange={(e) => {
            setQueryResponseTimeLimitMins(e.target.value);
          }}
          value={queryResponseTimeLimitMins}
        />
        <QueryAnalyticsGenericBarGraph
          dataPoints={queryCountGreaterThanInputResponseTimeLimitPoints}
        />
      </div>
      {isUnsatisfiedQueryListModalOpen && (
        <ShowUnsatisfiedQueryDetailsModal
          isModalOpen={isUnsatisfiedQueryListModalOpen}
          setIsModalOpen={setIsUnsatisfiedQueryListModalOpen}
          unSatisfiedQueriesList={unSatisfiedQueriesList || []}
        />
      )}
      {isTransferredQueryListModalOpen && (
        <ShowTransferredQueryDetailsModal
          isModalOpen={isTransferredQueryListModalOpen}
          setIsModalOpen={setIsTransferredQueryListModalOpen}
          transferredQueriesList={transferredQueriesList || []}
        />
      )}
      {isDuplicateQueryListModalOpen && (
        <ShowDuplicateQueryDetailsModal
          isModalOpen={isDuplicateQueryListModalOpen}
          setIsModalOpen={setIsDuplicateQueryListModalOpen}
          duplicateQueriesList={duplicateQueriesList || []}
        />
      )}
    </div>
  );
};

export default DiscordQueryAnalyticsDash;
